import React from 'react';
import { Control } from 'react-hook-form';
import { Trash } from 'lucide-react';
import { IProfileSchema } from '@area-restrita/client';

import InlineInput from '@/components/InlineInput';
import PageContainer from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormField } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import UfSelector from '@/components/QuerySelectors/UfSelector';
import CitySelector from '@/components/QuerySelectors/CitySelector';
import MaskedInput from '@/components/MaskedInput';
import Dialog from '@/components/template/Dialog';

import DeleteAccountDialog from '../../components/DeleteAccount';

interface IProps {
  control: Control<IProfileSchema>;
  isDirty: boolean;
  onReset: () => void;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}

const ProfileView: React.FC<IProps> = ({
  control,
  isDirty,

  onSubmit,
  onReset,
}) => (
  <PageContainer
    title="Perfil"
    subtitle="Aqui você pode gerenciar as configurações do seu perfil, como nome, e-mail e senha."
    withSeparator
  >
    <Card>
      <CardHeader>
        <CardTitle>Perfil de acesso</CardTitle>
      </CardHeader>

      <form onSubmit={onSubmit}>
        <CardContent className="p-0">
          <div className="p-6">
            <FormField
              control={control}
              name="fullName"
              render={({ field }) => (
                <InlineInput
                  title="Nome"
                  placeholder="Digite seu nome"
                  {...field}
                />
              )}
            />
            <FormField
              control={control}
              name="email"
              render={({ field }) => (
                <InlineInput
                  title="E-mail"
                  placeholder="Digite seu e-mail"
                  {...field}
                />
              )}
            />

            <FormField
              control={control}
              name="password"
              render={({ field }) => (
                <InlineInput
                  title="Senha"
                  type="password"
                  placeholder="Digite sua senha"
                  {...field}
                />
              )}
            />

            <FormField
              control={control}
              name="newPassword"
              render={({ field }) => (
                <InlineInput
                  title="Nova Senha"
                  type="password"
                  placeholder="Digite sua nova senha"
                  {...field}
                />
              )}
            />

            <FormField
              control={control}
              name="confirmPassword"
              render={({ field }) => (
                <InlineInput
                  title="Confirmar Senha"
                  type="password"
                  placeholder="Confirme sua nova senha"
                  {...field}
                />
              )}
            />
          </div>

          <Separator className="my-4" />

          <div className="px-6 pb-6">
            <FormField
              control={control}
              name="phone"
              render={({ field }) => (
                <InlineInput
                  title="Telefone"
                  placeholder="Digite seu telefone"
                  as={MaskedInput}
                  maskOptions={{
                    mask: '(00) 00000-0000',
                  }}
                  {...field}
                />
              )}
            />

            <FormField
              control={control}
              name="address.zipCode"
              render={({ field }) => (
                <InlineInput
                  title="CEP"
                  as={MaskedInput}
                  maskOptions={{
                    mask: '00000-000',
                  }}
                  placeholder="Digite seu CEP"
                  {...field}
                />
              )}
            />
            <FormField
              control={control}
              name="address.state"
              render={({ field: { ref: _, ...stateField } }) => (
                <FormField
                  control={control}
                  name="address.city"
                  render={({ field: { ref: __, ...cityField } }) => (
                    <>
                      <InlineInput
                        title="Estado"
                        as={UfSelector}
                        className="w-full"
                        {...stateField}
                        onChange={(option) => {
                          stateField.onChange(option);
                          cityField.onChange(undefined);
                        }}
                      />

                      <InlineInput
                        title="Cidade"
                        as={CitySelector}
                        stateId={stateField.value?.value}
                        className="w-full"
                        {...cityField}
                      />
                    </>
                  )}
                />
              )}
            />

            <FormField
              control={control}
              name="address.neighborhood"
              render={({ field }) => (
                <InlineInput
                  title="Bairro"
                  placeholder="Digite seu bairro"
                  {...field}
                />
              )}
            />
            <FormField
              control={control}
              name="address.street"
              render={({ field }) => (
                <InlineInput
                  title="Rua"
                  placeholder="Digite sua rua"
                  {...field}
                />
              )}
            />
            <FormField
              control={control}
              name="address.number"
              render={({ field }) => (
                <InlineInput
                  title="Número"
                  placeholder="Digite o número"
                  {...field}
                />
              )}
            />
            <FormField
              control={control}
              name="address.complement"
              render={({ field }) => (
                <InlineInput
                  title="Complemento"
                  placeholder="Digite o complemento"
                  {...field}
                />
              )}
            />
          </div>
        </CardContent>
        <Separator className="my-4" />
        <CardFooter className="flex justify-end gap-4">
          <Dialog
            title="Excluir conta"
            description={
              <p>
                Você tem certeza que deseja excluir sua conta?
                <br />
                <strong> Essa ação não pode ser desfeita.</strong>
              </p>
            }
            trigger={
              <Button className="hidden" type="button" variant="destructive">
                <Trash className="mr-2" />
                Excluir conta
              </Button>
            }
          >
            <DeleteAccountDialog />
          </Dialog>

          <div className="flex gap-4">
            <Button
              variant="secondary"
              disabled={!isDirty}
              onClick={onReset}
              type="reset"
            >
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </div>
        </CardFooter>
      </form>
    </Card>
  </PageContainer>
);

export default ProfileView;
